import { createFileRoute, Link } from "@tanstack/react-router";
import { createContext, lazy, Suspense, useContext, useEffect, useMemo, useState } from "react";
import { openCookiePreferences } from "@/components/CookieConsent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Zap, Check, Clock, ArrowRight, Rocket, Cog, Globe,
  CreditCard, Sparkles, Lock, X, Upload, Palette,
  Layout, FileEdit, KeyRound,
  ShieldCheck, MessageCircle, UserCheck, CheckCircle2,
  Mail, Star, Calculator, LayoutDashboard, Database,
  FolderOpen, Store, Calendar, Plus, Minus, Crown, Menu,
  Bug, TrendingUp, LifeBuoy, Timer, AlertCircle,
  type LucideIcon,
} from "lucide-react";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";
import filippoAsset from "@/assets/founder-filippo.webp.asset.json";

import { motion } from "framer-motion";
import { Reveal, RevealStagger } from "@/components/motion/Reveal";
import { PricingPremium } from "@/components/PricingPremium";

const TestimonialsSection = lazy(() => import("@/components/Testimonials").then(m => ({ default: m.TestimonialsSection })));
const ShowcaseSection = lazy(() => import("@/components/Showcase").then(m => ({ default: m.ShowcaseSection })));
const PartnersSection = lazy(() => import("@/components/Partners").then(m => ({ default: m.PartnersSection })));
const LiveActivitySection = lazy(() => import("@/components/LiveActivity").then(m => ({ default: m.LiveActivitySection })));
const HeroBackground = lazy(() => import("@/components/motion/HeroBackground").then(m => ({ default: m.HeroBackground })));



export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "FlashWeb Agency — Il tuo sito professionale in 48 ore" },
      { name: "description", content: "Agenzia svizzera. Siti web professionali a tariffa fissa, consegnati in 48 ore garantite. Configura il tuo sito ora." },
      { property: "og:title", content: "FlashWeb Agency — Siti web in 48 ore" },
      { property: "og:description", content: "Precisione svizzera. Tariffa fissa. Online in 48 ore o non lavoriamo insieme." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
          "@id": "https://www.flash-web.ch/#organization",
          name: "FlashWeb",
          legalName: "FVT FuturaVision Technology GmbH",
          url: "https://www.flash-web.ch",
          logo: "https://www.flash-web.ch/favicon.ico",
          image: "https://storage.googleapis.com/gpt-engineer-file-uploads/CsrkvcfQsFgTQbLNHYwqRzbKA2d2/social-images/social-1781874004744-Social_Image_FlashWeb.webp",
          description: "FlashWeb è un'agenzia web svizzera che crea siti professionali a tariffa fissa, consegnati in 48 ore. Serviamo aziende in Ticino, Svizzera e Italia.",
          telephone: "+41782369200",
          email: "info@flashweb.ch",
          priceRange: "CHF 990 – CHF 1'790",
          address: [
            {
              "@type": "PostalAddress",
              streetAddress: "Bareerstrasse 75",
              addressLocality: "Zug",
              postalCode: "6300",
              addressRegion: "Zug",
              addressCountry: "CH",
            },
            {
              "@type": "PostalAddress",
              streetAddress: "Via Ai Prati 2",
              addressLocality: "Bedano",
              addressRegion: "Ticino",
              addressCountry: "CH",
            },
          ],
          location: [
            {
              "@type": "Place",
              name: "FlashWeb — Sede Zug",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Bareerstrasse 75",
                addressLocality: "Zug",
                postalCode: "6300",
                addressRegion: "Zug",
                addressCountry: "CH",
              },
            },
            {
              "@type": "Place",
              name: "FlashWeb — Sede Bedano",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Via Ai Prati 2",
                addressLocality: "Bedano",
                addressRegion: "Ticino",
                addressCountry: "CH",
              },
            },
          ],
          areaServed: [
            { "@type": "AdministrativeArea", name: "Canton Ticino" },
            { "@type": "Country", name: "Svizzera" },
            { "@type": "Country", name: "Italia" },
          ],
          sameAs: [
            // TODO: aggiungere URL reali dei profili social / Google Business Profile
            "https://www.linkedin.com/company/flashweb-agency",
            "https://www.instagram.com/flashweb.agency",
          ],
          founder: [
            { "@type": "Person", name: "Filippo" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://www.flash-web.ch/#reviews",
          name: "FlashWeb",
          url: "https://www.flash-web.ch",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "65",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              author: { "@type": "Person", name: "Marco Rossi" },
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              reviewBody: "48 ore nette. Briefing completato il martedì, sito online il giovedì. Punto.",
            },
            {
              "@type": "Review",
              author: { "@type": "Person", name: "Laura Bianchi" },
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              reviewBody: "Professionali, veloci, zero sorprese sul prezzo. Il sito supera le aspettative.",
            },
            {
              "@type": "Review",
              author: { "@type": "Person", name: "Stefan Müller" },
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              reviewBody: "Swiss quality indeed. The site was live before my morning coffee got cold.",
            },
            {
              "@type": "Review",
              author: { "@type": "Person", name: "Matteo Colombo" },
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              reviewBody: "Il prezzo fisso mi ha salvato da un preventivo da CHF 8.000 di un'altra agenzia.",
            },
          ],
        }),
      },
    ],
  }),
  component: Landing,
});

const PACKAGES = {
  landing: { name: "Sito Landing", price: 990, originalPrice: 1490, desc: "Design professionale, copy orientato alla conversione e struttura ottimizzata per massimizzare richieste e vendite. Include 1 revisione." } as { name: string; price: number; originalPrice?: number; desc: string },
  business: { name: "Sito Premium", price: 1790, desc: "Sito completamente personalizzato con animazioni premium, design moderno, struttura su misura ed esperienza utente di alto livello. Include fino a 5 pagine e 2 revisioni." } as { name: string; price: number; originalPrice?: number; desc: string },
} as const;

const FLEX_WA_URL = "https://wa.me/41782369200?text=" + encodeURIComponent(
  "Ciao Filippo,\n\nVorrei attivare il piano FlashWeb Flex (CHF 59/mese, 0 CHF di attivazione).\n\nNome attività:\nSettore:\nTelefono:\n\nAttendo il vostro contatto."
);

// ----- Currency selector (CHF default, 1 EUR = 0.92 CHF) -----
type Currency = "CHF" | "EUR";
const EUR_PER_CHF = 1 / 0.92;
const CurrencyContext = createContext<{ currency: Currency; setCurrency: (c: Currency) => void }>({
  currency: "CHF",
  setCurrency: () => {},
});
function useCurrency() {
  const { currency, setCurrency } = useContext(CurrencyContext);
  const format = (chf: number, opts: { withCode?: boolean; suffix?: string } = {}) => {
    const { withCode = true, suffix = "" } = opts;
    const value = currency === "CHF" ? chf : Math.round(chf * EUR_PER_CHF);
    const s = value.toString();
    const formatted = s.length <= 3 ? s : s.slice(0, -3) + (currency === "CHF" ? "'" : ".") + s.slice(-3);
    return `${withCode ? currency + " " : ""}${formatted}${suffix}`;
  };
  return { currency, setCurrency, format };
}

type AddonGroup = "Servizi Extra" | "Piani Mensili" | "Soluzioni Business";
type Addon = {
  id: string;
  label: string;
  price: number;
  group: AddonGroup;
  period: string;
  desc: string;
  icon: LucideIcon;
  featured?: boolean;
  features?: readonly string[];
};

const ADDONS: readonly Addon[] = [
  // === Servizi Extra (una tantum) ===
  { id: "logo", label: "Logo Express", price: 99, group: "Servizi Extra", period: "una tantum", icon: Palette,
    desc: "Marchio professionale creato rapidamente per partire subito con un'identità visiva coerente." },
  { id: "tracking", label: "Tracking Avanzato", price: 80, group: "Servizi Extra", period: "una tantum", icon: Sparkles,
    desc: "Google Analytics, Meta Pixel, conversion tracking e cookie compliance." },
  { id: "gdpr", label: "Privacy & LPD/GDPR Compliance", price: 90, group: "Servizi Extra", period: "una tantum", icon: ShieldCheck,
    desc: "Privacy policy, cookie banner e documentazione conforme per Svizzera ed Europa." },
  { id: "i18n", label: "Multilingua", price: 190, group: "Servizi Extra", period: "una tantum", icon: Globe,
    desc: "Versione del sito in più lingue con struttura ottimizzata per il mercato internazionale." },
  { id: "booking", label: "Sistema Prenotazioni", price: 120, group: "Servizi Extra", period: "una tantum", icon: Calendar,
    desc: "Calendario online per appuntamenti, consulenze e prenotazioni automatiche." },
  { id: "newsletter", label: "Newsletter Ready", price: 120, group: "Servizi Extra", period: "una tantum", icon: Mail,
    desc: "Modulo iscrizione e integrazione con piattaforme email marketing." },
  { id: "reviews", label: "Google Reviews Booster", price: 149, group: "Servizi Extra", period: "una tantum", icon: Star,
    desc: "Sistema automatico per raccogliere e mostrare recensioni Google." },
  { id: "quote_calc", label: "Calcolatore Preventivo", price: 250, group: "Servizi Extra", period: "una tantum", icon: Calculator,
    desc: "Configuratore interattivo che genera preventivi e acquisisce lead qualificati." },
  { id: "chat_ai", label: "Chat AI per il tuo sito", price: 190, group: "Servizi Extra", period: "una tantum", icon: MessageCircle,
    desc: "Assistente virtuale addestrato sulla tua attività per rispondere ai clienti 24/7." },
  { id: "blog_seo", label: "Blog SEO Automatico", price: 150, group: "Servizi Extra", period: "una tantum", icon: FileEdit,
    desc: "Sistema per pubblicare articoli ottimizzati SEO e aumentare la visibilità su Google." },

  // === Piani Mensili ===
  { id: "flashcare", label: "FlashCare", price: 49, group: "Piani Mensili", period: "/mese", icon: Rocket, featured: true,
    desc: "Aggiornamenti, monitoraggio e supporto continuativo per mantenere il tuo sito sempre al massimo.",
    features: [
      "Backup automatici",
      "Aggiornamenti tecnici",
      "Monitoraggio del sito",
      "Supporto prioritario",
      "Fino a 2 ore di modifiche al mese",
    ] },

  // === Soluzioni Business ===
  { id: "crm", label: "CRM Express", price: 800, group: "Soluzioni Business", period: "una tantum", icon: LayoutDashboard,
    desc: "Dashboard privata per gestire contatti, richieste e follow-up." },
  { id: "recruiting", label: "Recruiting System", price: 350, group: "Soluzioni Business", period: "una tantum", icon: UserCheck,
    desc: "Pagina \"Lavora con noi\" con raccolta CV e gestione candidature." },
] as const;

const ENTERPRISE_SOLUTIONS = [
  { id: "internal_app", title: "App Interna Aziendale", icon: Cog,
    desc: "Software personalizzato per digitalizzare processi interni." },
  { id: "custom_crm", title: "CRM Personalizzato", icon: Database,
    desc: "Gestione clienti, pipeline commerciali e automazioni su misura." },
  { id: "client_portal", title: "Portale Clienti", icon: FolderOpen,
    desc: "Area riservata con login, documenti, fatture e comunicazioni." },
  { id: "marketplace", title: "Marketplace di Servizi", icon: Store,
    desc: "Piattaforma completa con utenti, pagamenti e dashboard." },
] as const;

const ENTERPRISE_CONTACT = "mailto:info@flashweb.ch?subject=Richiesta%20consulenza%20soluzione%20enterprise";

type DomainSelection = { name: string };

function Section({ id, children, className = "" }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={`relative mx-auto w-full max-w-6xl px-6 py-14 sm:py-20 ${className}`}>
      {children}
    </section>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
      <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
      {children}
    </div>
  );
}

// ----- Pricing subcomponents -----
function PricingCard({
  title, price, oldPrice, discountLabel, description, features, ctaLabel, onCta, accent = false,
}: {
  title: string; price: string; oldPrice?: string; discountLabel?: string; description: string;
  features: string[]; ctaLabel: string; onCta: () => void; accent?: boolean;
}) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className={`group relative flex h-full flex-col rounded-3xl border p-8 backdrop-blur transition-shadow ${
        accent
          ? "border-primary/30 bg-gradient-to-b from-card/80 to-card/40 hover:shadow-[0_0_40px_-10px_var(--primary)]"
          : "border-border/60 bg-card/40 hover:border-primary/30"
      }`}
    >
      {discountLabel && (
        <div className="absolute -top-3 right-6 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-950 shadow-lg shadow-emerald-500/30 ring-1 ring-emerald-300/60">
          🔥 Promo {discountLabel}
        </div>
      )}
      <h3 className="font-display text-xl font-semibold">{title}</h3>
      <div className="mt-4 flex flex-wrap items-baseline gap-2">
        <span className="font-display text-4xl font-bold">{price}</span>
        {oldPrice && <span className="text-sm text-muted-foreground line-through opacity-70">{oldPrice}</span>}
        {discountLabel && (
          <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-bold text-emerald-300 ring-1 ring-emerald-500/40">
            {discountLabel}
          </span>
        )}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{description}</p>
      <ul className="mt-6 flex-1 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button variant={accent ? "hero" : "outlineHero"} size="lg" className="mt-8 w-full" onClick={onCta}>
        {ctaLabel} <ArrowRight />
      </Button>
    </motion.div>
  );
}


function FlexCard({ waUrl, priceLabel }: { waUrl: string; priceLabel: string }) {
  const features = [
    "Nessun costo iniziale",
    "Sito online in 48 ore",
    "Hosting incluso",
    "Dominio incluso",
    "Certificato SSL",
    "Aggiornamenti inclusi",
    "Backup automatici",
    "Assistenza continua",
    "Modifiche mensili",
    "SEO base",
    "Email professionale",
  ];
  return (
    <motion.div
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 240, damping: 20 }}
      className="relative lg:-my-4 lg:scale-[1.03]"
    >
      {/* animated glow */}
      <div className="pointer-events-none absolute -inset-px rounded-[26px] bg-gradient-to-br from-primary/60 via-fuchsia-500/40 to-primary/60 opacity-70 blur-[2px]" />
      <div className="pointer-events-none absolute -inset-4 rounded-[32px] bg-primary/10 opacity-60 blur-2xl" />

      <div className="relative flex h-full flex-col rounded-3xl border border-primary/50 bg-gradient-to-b from-card via-card/90 to-card/70 p-8 shadow-[0_20px_60px_-15px_var(--primary)] backdrop-blur">
        {/* badges */}
        <div className="absolute -top-3 left-1/2 flex -translate-x-1/2 gap-2">
          <span className="rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-lg">
            🔥 Più scelto
          </span>
          <span className="rounded-full bg-background/80 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary ring-1 ring-primary/40 backdrop-blur">
            Novità
          </span>
        </div>

        <h3 className="mt-2 font-display text-2xl font-bold">FlashWeb Flex</h3>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="font-display text-5xl font-bold text-gradient">{priceLabel}</span>
        </div>
        <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
          <Sparkles className="h-4 w-4" /> 0 CHF di attivazione
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Il tuo sito professionale senza investimento iniziale. Lo realizziamo in 48 ore e ci occupiamo noi di manutenzione, aggiornamenti, hosting e assistenza. Tu pensi al tuo business.
        </p>

        <ul className="mt-6 grid flex-1 grid-cols-1 gap-2.5 sm:grid-cols-2">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <motion.a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-fuchsia-500 px-6 py-3.5 font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-shadow hover:shadow-primary/50"
        >
          Inizia ora <ArrowRight className="h-4 w-4" />
        </motion.a>

        <p className="mt-3 text-center text-xs text-muted-foreground">Permanenza minima 24 mesi.</p>
      </div>
    </motion.div>
  );
}

function CompCell({ v, highlight = false }: { v: boolean | string; highlight?: boolean }) {
  const base = `px-4 py-4 text-center ${highlight ? "bg-primary/[0.04] font-medium text-foreground" : ""}`;
  if (v === true) return <td className={base}><Check className="mx-auto h-4 w-4 text-primary" /></td>;
  if (v === false) return <td className={base}><X className="mx-auto h-4 w-4 text-muted-foreground/40" /></td>;
  return <td className={base}><span className={highlight ? "text-primary" : "text-muted-foreground"}>{v}</span></td>;
}

// ----- Support Portal Section -----
const SUPPORT_CATEGORIES: { icon: LucideIcon; emoji: string; title: string; desc: string }[] = [
  { icon: Bug, emoji: "🐞", title: "Segnalazione Bug", desc: "Individuiamo e risolviamo malfunzionamenti in tempi rapidi." },
  { icon: FileEdit, emoji: "✏️", title: "Modifiche al sito", desc: "Testi, immagini, sezioni: manteniamo il tuo sito sempre aggiornato." },
  { icon: Rocket, emoji: "🚀", title: "Nuove funzionalità", desc: "Espandi il sito con moduli, integrazioni e automazioni su misura." },
  { icon: TrendingUp, emoji: "📈", title: "SEO e Performance", desc: "Ottimizzazione continua per velocità, ranking e conversioni." },
  { icon: ShieldCheck, emoji: "🔒", title: "Sicurezza", desc: "Aggiornamenti, monitoraggio e backup automatici garantiti." },
  { icon: MessageCircle, emoji: "💬", title: "Supporto generale", desc: "Un team dedicato a rispondere a ogni tua domanda." },
];

const SUPPORT_TICKETS = [
  { id: "FW-2041", title: "Modifica Hero Homepage", status: "in-progress" as const, label: "In lavorazione", color: "amber" },
  { id: "FW-2040", title: "Aggiornamento listino prezzi", status: "done" as const, label: "Completato", color: "green" },
  { id: "FW-2038", title: "Nuova pagina FAQ", status: "review" as const, label: "In revisione", color: "blue" },
];

const SUPPORT_PRICING_SMALL = [
  { label: "Cambio testo", price: 15 },
  { label: "Cambio immagine", price: 20 },
  { label: "Aggiornamento contatti", price: 10 },
  { label: "Inserimento recensioni", price: 25 },
  { label: "Nuova sezione", price: 40 },
  { label: "Nuova pagina", price: 80 },
];
const SUPPORT_PRICING_ADVANCED = [
  { label: "SEO pagina", price: 35 },
  { label: "Blog", price: 120 },
  { label: "Multilingua", price: 190 },
  { label: "CRM", price: 250 },
  { label: "Nuove funzionalità", price: 120, from: true },
];

function SupportPortalSection() {
  const { format } = useCurrency();
  return (
    <Section id="support">
      <Reveal>
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <Eyebrow>Portale Assistenza FlashWeb</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
            Non ti lasciamo mai <span className="text-gradient">da solo</span>.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Dopo la messa online, continuiamo a seguirti: assistenza rapida, ticket tracciati e un team dedicato per far crescere il tuo sito nel tempo.
          </p>
        </div>
      </Reveal>

      {/* CATEGORIES */}
      <RevealStagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" stagger={0.08}>
        {SUPPORT_CATEGORIES.map((c) => (
          <motion.div
            key={c.title}
            variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
            whileHover={{ y: -4 }}
            className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur transition-colors hover:border-primary/40"
          >
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
            <div className="relative">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-xl ring-1 ring-primary/20">
                <span aria-hidden>{c.emoji}</span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
            </div>
          </motion.div>
        ))}
      </RevealStagger>

    </Section>
  );
}

function StatChip({ label, value, tone }: { label: string; value: string; tone: "amber" | "blue" | "green" }) {
  const tones = {
    amber: "from-amber-500/20 to-amber-500/5 text-amber-300 ring-amber-500/30",
    blue: "from-blue-500/20 to-blue-500/5 text-blue-300 ring-blue-500/30",
    green: "from-emerald-500/20 to-emerald-500/5 text-emerald-300 ring-emerald-500/30",
  };
  return (
    <div className={`rounded-xl bg-gradient-to-b ${tones[tone]} p-4 ring-1`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold">{value}</div>
    </div>
  );
}

function TicketBadge({ label, tone }: { label: string; tone: "amber" | "green" | "blue" }) {
  const map = {
    amber: { dot: "bg-amber-400", ring: "ring-amber-500/30", text: "text-amber-300", bg: "bg-amber-500/10" },
    green: { dot: "bg-emerald-400", ring: "ring-emerald-500/30", text: "text-emerald-300", bg: "bg-emerald-500/10" },
    blue: { dot: "bg-blue-400", ring: "ring-blue-500/30", text: "text-blue-300", bg: "bg-blue-500/10" },
  }[tone];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full ${map.bg} px-2.5 py-1 text-xs font-medium ${map.text} ring-1 ${map.ring}`}>
      <span className={`relative flex h-1.5 w-1.5`}>
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${map.dot} opacity-70`} />
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${map.dot}`} />
      </span>
      {label}
    </span>
  );
}

function PriceRow({ label, price }: { label: string; price: string }) {
  return (
    <li className="flex items-center justify-between py-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{price}</span>
    </li>
  );
}



// ----- Delivery time widget -----
function useDeliveryTime() {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    setNow(new Date());
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!now) return { day: "", date: "", time: "", ready: false };
    // 48 ore reali, saltando sabato e domenica
    const HOUR = 60 * 60 * 1000;
    let remaining = 48 * HOUR;
    const d = new Date(now.getTime());
    while (remaining > 0) {
      const day = d.getDay(); // 0 Sun .. 6 Sat
      if (day === 0 || day === 6) {
        // salta al lunedì stessa ora
        d.setDate(d.getDate() + (day === 0 ? 1 : 2));
        continue;
      }
      // prossimo confine: mezzanotte di questo giorno
      const nextMidnight = new Date(d);
      nextMidnight.setHours(24, 0, 0, 0);
      const chunk = nextMidnight.getTime() - d.getTime();
      if (remaining <= chunk) {
        d.setTime(d.getTime() + remaining);
        remaining = 0;
      } else {
        d.setTime(nextMidnight.getTime());
        remaining -= chunk;
      }
    }
    const day = d.toLocaleDateString("it-IT", { weekday: "long" });
    const date = d.toLocaleDateString("it-IT", { day: "2-digit", month: "long" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return { day: day.charAt(0).toUpperCase() + day.slice(1), date, time, ready: true };
  }, [now]);


}

function CountdownTimer() {
  const [t, setT] = useState({ h: 47, m: 59, s: 59 });
  useEffect(() => {
    const start = Date.now();
    const total = 48 * 3600;
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      const remaining = total - (elapsed % total);
      setT({
        h: Math.floor(remaining / 3600),
        m: Math.floor((remaining % 3600) / 60),
        s: remaining % 60,
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n: number) => n.toString().padStart(2, "0");
  const Cell = ({ v, l }: { v: number; l: string }) => (
    <div className="flex flex-col items-center">
      <div className="relative overflow-hidden rounded-xl border border-primary/30 bg-background/80 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
        <div
          key={v}
          className="relative font-display text-4xl font-bold tabular-nums tracking-tight text-foreground animate-fade-in sm:text-6xl"
        >
          {pad(v)}
        </div>
      </div>
      <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground sm:text-xs">{l}</div>
    </div>
  );
  return (
    <div className="flex items-center justify-center gap-3 sm:gap-5">
      <Cell v={t.h} l="Ore" />
      <span className="font-display text-3xl font-bold text-primary/80 sm:text-5xl">:</span>
      <Cell v={t.m} l="Minuti" />
      <span className="font-display text-3xl font-bold text-primary/80 sm:text-5xl">:</span>
      <Cell v={t.s} l="Secondi" />
    </div>
  );
}









function Landing() {
  const delivery = useDeliveryTime();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<"landing" | "business">("business");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [deferHeavy, setDeferHeavy] = useState(false);
  const [currency, setCurrency] = useState<Currency>("CHF");
  const [lang, setLang] = useState<"IT" | "EN" | "DE" | "FR">("IT");
  const { format } = (() => {
    // local format using current state (Provider downstream will give same fn via context)
    const f = (chf: number, opts: { withCode?: boolean; suffix?: string } = {}) => {
      const { withCode = true, suffix = "" } = opts;
      const value = currency === "CHF" ? chf : Math.round(chf * EUR_PER_CHF);
      const s = value.toString();
      const formatted = s.length <= 3 ? s : s.slice(0, -3) + (currency === "CHF" ? "'" : ".") + s.slice(-3);
      return `${withCode ? currency + " " : ""}${formatted}${suffix}`;
    };
    return { format: f };
  })();

  useEffect(() => {
    const cb = () => setDeferHeavy(true);
    const idle = (window as any).requestIdleCallback;
    const id = idle ? idle(cb, { timeout: 2500 }) : window.setTimeout(cb, 1800);
    const onInteract = () => cb();
    window.addEventListener("pointerdown", onInteract, { once: true, passive: true });
    window.addEventListener("scroll", onInteract, { once: true, passive: true });
    return () => {
      if (idle && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(id);
      else window.clearTimeout(id as number);
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("scroll", onInteract);
    };
  }, []);

  // Auto-open checkout when arriving from /prezzi with ?pkg=landing|business
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const pkg = params.get("pkg");
    if (pkg === "landing" || pkg === "business") {
      setSelectedPkg(pkg);
      setCheckoutOpen(true);
      // clean URL so refresh doesn't re-trigger
      const url = new URL(window.location.href);
      url.searchParams.delete("pkg");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  function openCheckout(pkg: "landing" | "business") {
    setSelectedPkg(pkg);
    setCheckoutOpen(true);
  }

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency }}>
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV */}
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <a href="#top" className="flex min-w-0 items-center gap-2 group">
            <img src={logoAsset.url} alt="FlashWeb Agency" width="144" height="36" fetchPriority="high" decoding="async" className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105" />
            <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
          </a>

          <div className="flex items-center gap-2">


            <a
              href="/portal"
              className="hidden items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Area clienti
            </a>

            <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
              <a href="https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AVorrei%20ricevere%20il%20mio%20sito%20professionale%20online%20in%2048%20ore.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20vostro%20contatto." target="_blank" rel="noopener noreferrer">
                Contattaci <ArrowRight />
              </a>
            </Button>
            <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
              <a href="https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AVorrei%20ricevere%20il%20mio%20sito%20professionale%20online%20in%2048%20ore.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20vostro%20contatto." target="_blank" rel="noopener noreferrer">
                Contattaci <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
            <button
              type="button"
              aria-label={mobileNavOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((v) => !v)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

          </div>
        </div>
        {mobileNavOpen && (
          <>
            <div
              className="fixed inset-0 top-16 z-40 bg-background/60 backdrop-blur-sm"
              onClick={() => setMobileNavOpen(false)}
              aria-hidden="true"
            />
            <nav className="fixed right-0 top-16 z-50 h-[calc(100vh-4rem)] w-[min(88vw,320px)] overflow-y-auto border-l border-border/50 bg-background/95 backdrop-blur-xl shadow-2xl animate-in slide-in-from-right duration-200">
              <div className="flex flex-col px-4 py-4 text-sm">
                {[
                  { href: "/servizi", label: "Servizi" },
                  { href: "/prezzi", label: "Prezzi" },
                  { href: "/come-funziona", label: "Come funziona" },
                  { href: "/portfolio", label: "Portfolio" },
                  { href: "/recensioni", label: "Recensioni" },
                  { href: "/trasparenza", label: "Trasparenza" },
                  { href: "/chi-siamo", label: "Chi siamo" },
                  { href: "/faq", label: "FAQ" },
                  { href: "/blog", label: "Blog" },
                  { href: "/contatti", label: "Contatti" },
                  { href: "/portal", label: "Area clienti" },
                ].map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setMobileNavOpen(false)}
                    className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="mt-3 flex items-center justify-between rounded-xl border border-border/50 bg-card/50 p-3">
                  <span className="text-sm text-muted-foreground">Valuta</span>
                  <div className="flex items-center gap-0.5 rounded-full border border-border/60 bg-background p-0.5">
                    {(["CHF", "EUR"] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCurrency(c)}
                        aria-pressed={currency === c}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${currency === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between rounded-xl border border-border/50 bg-card/50 p-3">
                  <span className="text-sm text-muted-foreground">Lingua</span>
                  <div className="flex items-center gap-0.5 rounded-full border border-border/60 bg-background p-0.5">
                    {(["IT", "EN", "DE", "FR"] as const).map((l) => (
                      <button
                        key={l}
                        type="button"
                        onClick={() => setLang(l)}
                        aria-pressed={lang === l}
                        title={l === "IT" ? "Italiano" : l === "EN" ? "English (coming soon)" : l === "DE" ? "Deutsch (coming soon)" : "Français (coming soon)"}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  variant="hero"
                  size="default"
                  asChild
                  className="mt-2"
                >
                  <a href="https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AVorrei%20ricevere%20il%20mio%20sito%20professionale%20online%20in%2048%20ore.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20vostro%20contatto." target="_blank" rel="noopener noreferrer" onClick={() => setMobileNavOpen(false)}>
                    Contattaci <ArrowRight />
                  </a>
                </Button>
              </div>
            </nav>
          </>
        )}
      </header>

      {/* 1. HERO */}
      <div id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        {deferHeavy && <Suspense fallback={null}><HeroBackground /></Suspense>}
        <Section className="!py-20 sm:!py-28 text-center">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-7">
            <Eyebrow>Mentre gli altri aspettano settimane, tu sei già online.</Eyebrow>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
              Il tuo sito professionale.
              <br />
              Online in{" "}
              <span className="text-gradient inline-block">48 ore</span>.
            </h1>
            <p className="max-w-xl text-lg text-muted-foreground sm:text-xl">
              Tariffa fissa. Nessuna sorpresa. Consegna garantita.
            </p>

            <div className="mt-2 flex flex-col items-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" onClick={() => openCheckout("landing")} className="fw-shimmer">
                Configura il tuo sito <ArrowRight />
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href="#showcase">Guarda gli esempi</a>
              </Button>
            </div>

            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-xs text-muted-foreground backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span>Online entro <span className="font-semibold text-foreground">{delivery.ready ? `${delivery.day} ${delivery.date} alle ${delivery.time}` : "48 ore"}</span></span>
            </div>
          </div>
        </Section>

      </div>


      {/* 2. SOCIAL PROOF MINI */}
      <Section className="!py-12 sm:!py-16">
        <RevealStagger className="grid gap-6 rounded-2xl border border-border bg-card/40 p-8 text-center sm:grid-cols-3 sm:p-10">
          {[
            { n: "60+", l: "Siti consegnati" },
            { n: "4.9/5", l: "Valutazione media" },
            { n: "48h", l: "Tempo medio di consegna" },
          ].map(s => (
            <motion.div
              key={s.l}
              variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } }}
              whileHover={{ y: -4 }}
              className="rounded-xl p-2 transition-colors hover:bg-background/30"
            >
              <div className="font-display text-4xl font-bold text-gradient sm:text-5xl">
                {s.n}
                {s.n === "4.9/5" && (
                  <span className="ml-1 text-2xl text-gradient sm:text-3xl">★</span>
                )}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{s.l}</div>
            </motion.div>
          ))}
        </RevealStagger>
        <Reveal delay={0.2}>
          <p className="mt-6 text-center text-lg text-muted-foreground">
            Costruiamo siti che <span className="font-semibold text-foreground">convertono</span>, non semplici pagine web.
          </p>
        </Reveal>
      </Section>

      {/* 3. PRICE — Premium redesign */}
      <PricingPremium
        format={format}
        onOrderLanding={() => openCheckout("landing")}
        onOrderPremium={() => openCheckout("business")}
        flexWaUrl={FLEX_WA_URL}
        enterpriseUrl={ENTERPRISE_CONTACT}
      />

      <SupportPortalSection />


      {/* 4. HOW IT WORKS — 3 step */}
      <Section id="how">
        <Reveal>
          <div className="mb-10 text-center">
            <Eyebrow>Come funziona</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-bold sm:text-5xl">Tre step. Quarantotto ore.</h2>
          </div>
        </Reveal>
        <RevealStagger className="grid gap-6 md:grid-cols-3" stagger={0.15}>
          {[
            { icon: CreditCard, n: "01", t: "Ordini il sito", d: "Configurazione rapida e pagamento sicuro. Briefing guidato." },
            { icon: Cog, n: "02", t: "Lo costruiamo in 48h", d: "Design, contenuti, ottimizzazioni. Tutto seguito personalmente." },
            { icon: Globe, n: "03", t: "Lo pubblichiamo online", d: "Dominio, hosting, SEO, tracciamenti. Vai live." },
          ].map(({ icon: Icon, n, t, d }) => (
            <motion.div
              key={n}
              variants={{ hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } }}
              whileHover={{ y: -6 }}
              className="relative rounded-2xl border border-border bg-card p-6 fw-lift hover:border-primary/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <span className="font-display text-2xl font-bold text-muted-foreground">{n}</span>
              </div>
              <h3 className="mt-4 text-lg font-semibold">{t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </motion.div>
          ))}
        </RevealStagger>
        <Reveal delay={0.2}>
          <div className="mt-10 flex justify-center">
            <Link
              to="/come-funziona"
              className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-all hover:border-primary/60 hover:bg-primary/10 hover:text-primary"
            >
              Vedi il processo completo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </Reveal>
      </Section>


      {/* 5. SHOWCASE */}
      <Suspense fallback={<div className="min-h-[400px]" />}><ShowcaseSection /></Suspense>

      {/* 6. LIVE ACTIVITY */}
      <Suspense fallback={null}><LiveActivitySection /></Suspense>

      {/* 7. TESTIMONIALS */}
      <Suspense fallback={null}><TestimonialsSection /></Suspense>


      {/* 8. FOUNDER — simplified */}
      <Section id="founder">
        <div className="rounded-3xl border border-border bg-card p-8 sm:p-12">
          <div className="grid items-center gap-10 md:grid-cols-[auto,1fr]">
            <div className="flex justify-center gap-4">
              {[
                { name: "Filippo", img: filippoAsset.url, delay: "0s" },
              ].map(f => (
                <div key={f.name} className="relative fw-float" style={{ animationDelay: f.delay }}>
                  <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-primary to-accent opacity-70 blur-md" />
                  <img src={f.img} alt={f.name} width="112" height="112" loading="lazy" decoding="async" className="relative h-24 w-24 rounded-full object-cover ring-4 ring-background sm:h-28 sm:w-28" />
                </div>
              ))}
            </div>
            <div>
              <Eyebrow>Il fondatore</Eyebrow>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
                Filippo. <span className="text-gradient">Sempre qui.</span>
              </h2>
              <p className="mt-3 text-muted-foreground">
                Ogni progetto è seguito personalmente. Niente call center, niente intermediari.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {[
                  { icon: MessageCircle, t: "Contatto diretto" },
                  { icon: UserCheck, t: "Nessun intermediario" },
                  { icon: ShieldCheck, t: "Supervisione personale" },
                ].map(({ icon: Icon, t }) => (
                  <div key={t} className="flex items-center gap-2 rounded-xl border border-border bg-background/40 px-3 py-2.5 text-sm">
                    <Icon className="h-4 w-4 text-primary" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Founder quotes */}
          <div className="mt-8 grid gap-4">
            {[
              {
                name: "Filippo Strazzanti",
                role: "Founder & Growth Lead",
                img: filippoAsset.url,
                quote: "Ho creato FlashWeb perché ottenere un sito professionale debba essere semplice: prezzo chiaro, tempi certi e nessuna sorpresa.",
              },
            ].map((f) => (
              <figure key={f.name} className="relative rounded-2xl border border-border bg-background/40 p-6 backdrop-blur">
                <blockquote className="font-display text-base leading-relaxed text-foreground/90 sm:text-lg">
                  "{f.quote}"
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-border/60 pt-4">
                  <img src={f.img} alt={f.name} width="40" height="40" loading="lazy" decoding="async" className="h-10 w-10 rounded-full object-cover ring-2 ring-primary/30" />
                  <div>
                    <div className="text-sm font-semibold">{f.name}</div>
                    <div className="text-xs text-muted-foreground">{f.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </Section>

      {/* 9. PARTNERS */}
      <Suspense fallback={null}><PartnersSection /></Suspense>

      {/* 10. FINAL CTA */}
      <div className="relative overflow-hidden border-t border-border">
        <div className="absolute inset-0 fw-mesh-shift" style={{ background: "var(--gradient-hero)" }} />
        <Section className="relative text-center">
          <motion.div
            initial={{ y: 80, opacity: 0, rotate: -15 }}
            whileInView={{ y: 0, opacity: 1, rotate: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ type: "spring", stiffness: 90, damping: 12 }}
          >
            <Rocket className="mx-auto h-10 w-10 text-primary" />
          </motion.div>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-bold sm:text-6xl">
              Il tuo sito può essere online <span className="text-gradient">in 48 ore</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 text-muted-foreground">Da {format(990)} una tantum oppure {format(59)}/mese con FlashWeb Flex.</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Button
                  variant="hero"
                  size="xl"
                  onClick={() => openCheckout("landing")}
                  className="fw-shimmer"
                >
                  Ricevi la tua bozza <ArrowRight />
                </Button>

              </motion.div>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-xs font-medium text-muted-foreground">
              <Zap className="h-3.5 w-3.5 text-primary fw-glow-pulse" />
              Slot disponibili limitati questa settimana
            </p>
          </Reveal>
        </Section>
      </div>


      <footer className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" loading="lazy" decoding="async" className="h-7 w-auto" />
                <span className="font-display text-sm font-bold">FlashWeb Agency</span>
              </div>
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} — Tutti i diritti riservati.
              </p>
              <p className="text-sm text-muted-foreground">
                Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
              </p>
            </div>
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  <span className="font-semibold text-foreground">Bareerstrasse 75</span><br />
                  6300 Zug, Svizzera
                </p>
                <p>
                  <span className="font-semibold text-foreground">Via Ai Prati 2</span><br />
                  Bedano, Svizzera
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  <a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a>
                </p>
                <p>
                  <a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a>
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
              <p className="text-sm text-muted-foreground">
                N° registro commercio:<br />
                <span className="font-semibold text-foreground">CH-170.4.022.832-9</span>
              </p>
              <div className="flex flex-col gap-1.5 pt-1 text-sm">
                <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
                <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
                <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
                <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
                <button
                  type="button"
                  onClick={() => openCookiePreferences()}
                  className="text-left text-muted-foreground hover:text-foreground"
                >
                  Preferenze cookie
                </button>
              </div>
            </div>
          </div>
          <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
            <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
            <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
            <Link to="/faq" className="hover:text-foreground">FAQ</Link>
            <Link to="/blog" className="hover:text-foreground">Blog</Link>
            <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
          </nav>
          <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
            <p>Realizzato con precisione svizzera 🇨🇭</p>
          </div>
        </div>
      </footer>

      {checkoutOpen && (
        <CheckoutFlow
          initialPkg={selectedPkg}
          onClose={() => setCheckoutOpen(false)}
        />
      )}

      <a
        href="https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AVorrei%20ricevere%20il%20mio%20sito%20professionale%20online%20in%2048%20ore.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20vostro%20contatto."
        target="_blank"
        rel="noopener noreferrer"
        className="fw-bounce fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2"
        aria-label="Chatta su WhatsApp"
        title="Chatta su WhatsApp"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-7 w-7"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      <style>{`
        @keyframes grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 15px var(--primary); }
          50% { box-shadow: 0 0 25px var(--primary), 0 0 40px var(--primary); }
        }
      `}</style>
    </div>
    </CurrencyContext.Provider>
  );
}


// ===================== CHECKOUT + ONBOARDING =====================

type Step = "checkout" | "request" | "submitted";

function CheckoutFlow({ initialPkg, onClose }: { initialPkg: "landing" | "business"; onClose: () => void }) {
  const [step, setStep] = useState<Step>("checkout");
  const [pkg, setPkg] = useState<"landing" | "business">(initialPkg);
  const [addons, setAddons] = useState<Record<string, boolean>>({});
  const [domain, setDomain] = useState<DomainSelection | null>(null);

  const subtotal = useMemo(() => {
    const base = PACKAGES[pkg].price;
    const add = ADDONS.reduce((s, a) => s + (addons[a.id] ? a.price : 0), 0);
    return base + add;
  }, [pkg, addons]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-background/80 p-4 backdrop-blur-md sm:p-8">
      <div className="relative w-full max-w-5xl rounded-2xl border border-border bg-card shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full border border-border bg-background/80 p-2 text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4" />
        </button>

        {step === "checkout" && (
          <CheckoutStep
            pkg={pkg} setPkg={setPkg}
            addons={addons} setAddons={setAddons}
            domain={domain} setDomain={setDomain}
            subtotal={subtotal}
            onNext={() => setStep("request")}
          />
        )}
        {step === "request" && (
          <RequestStep
            subtotal={subtotal}
            pkg={pkg}
            addons={addons}
            domain={domain}
            onNext={() => setStep("submitted")}
          />
        )}
        {step === "submitted" && (
          <SubmittedStep onClose={onClose} />
        )}
      </div>
    </div>
  );
}

function CheckoutStep({
  pkg, setPkg, addons, setAddons, domain, setDomain, subtotal, onNext,
}: {
  pkg: "landing" | "business";
  setPkg: (p: "landing" | "business") => void;
  addons: Record<string, boolean>;
  setAddons: (a: Record<string, boolean>) => void;
  domain: DomainSelection | null;
  setDomain: (d: DomainSelection | null) => void;
  subtotal: number;
  onNext: () => void;
}) {
  const { format } = useCurrency();
  const toggle = (id: string) => setAddons({ ...addons, [id]: !addons[id] });
  const extras = ADDONS.filter(a => a.group === "Servizi Extra");
  const plans = ADDONS.filter(a => a.group === "Piani Mensili");
  const business = ADDONS.filter(a => a.group === "Soluzioni Business");

  return (
    <div className="p-6 sm:p-8">
      <Eyebrow>Step 1 di 3 · Configurazione</Eyebrow>
      <h3 className="mt-3 text-2xl font-bold sm:text-3xl">Configura il tuo sito</h3>
      <p className="mt-1 text-sm text-muted-foreground">Scegli il pacchetto base e aggiungi solo ciò che ti serve.</p>

      {/* Pacchetto base */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {(["landing", "business"] as const).map(k => (
          <button
            key={k}
            onClick={() => setPkg(k)}
            className={`rounded-xl border p-4 text-left transition-colors ${pkg === k ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold">{PACKAGES[k].name}</span>
              <span className="flex shrink-0 items-center gap-1.5">
                {PACKAGES[k].originalPrice && (
                  <span className="text-xs text-muted-foreground line-through opacity-60">{format(PACKAGES[k].originalPrice!)}</span>
                )}
                <span className="text-sm font-bold text-primary">{format(PACKAGES[k].price)}</span>
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{PACKAGES[k].desc}</p>
          </button>
        ))}
      </div>

      {/* DOMINIO */}
      <SectionHeader eyebrow="Dominio incluso per te" title="Acquisto dominio" subtitle="Cerchiamo, acquistiamo e configuriamo il dominio per il tuo nuovo sito. Tu scegli il nome, noi pensiamo al resto." />
      <DomainSection domain={domain} setDomain={setDomain} />

      {/* SERVIZI EXTRA */}
      <SectionHeader eyebrow="Upsell" title="Servizi Extra" subtitle="Personalizza il tuo progetto con moduli pronti all'uso." />
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {extras.map(a => (
          <AddonCard key={a.id} addon={a} active={!!addons[a.id]} onToggle={() => toggle(a.id)} />
        ))}
      </div>

      {/* PIANI MENSILI */}
      <SectionHeader eyebrow="Ricorrente" title="Piani Mensili" subtitle="Manutenzione e supporto continuativo, in abbonamento." />
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map(a => (
          <AddonCard key={a.id} addon={a} active={!!addons[a.id]} onToggle={() => toggle(a.id)} />
        ))}
      </div>

      {/* SOLUZIONI BUSINESS */}
      <SectionHeader eyebrow="Business" title="Soluzioni Business" subtitle="Strumenti su misura per gestire la tua attività." />
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {business.map(a => (
          <AddonCard key={a.id} addon={a} active={!!addons[a.id]} onToggle={() => toggle(a.id)} />
        ))}
      </div>

      {/* SOLUZIONI ENTERPRISE */}
      <SectionHeader eyebrow="Enterprise · Su richiesta" title="Soluzioni Enterprise" subtitle="Progetti custom con preventivo dedicato." />
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ENTERPRISE_SOLUTIONS.map(s => (
          <EnterpriseCard key={s.id} solution={s} />
        ))}
      </div>

      {/* Totale */}
      <div className="mt-8 rounded-xl border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Totale</span>
          <span className="font-display text-3xl font-bold text-gradient">{format(subtotal)}</span>
        </div>
        {domain && (
          <div className="mt-3 flex items-center justify-between border-t border-primary/20 pt-3 text-xs text-muted-foreground">
            <span>Dominio richiesto: <span className="font-semibold text-foreground">{domain.name}</span></span>
          </div>
        )}
      </div>
      {domain && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Il dominio verrà acquistato e configurato da FlashWeb Agency. Prezzo su preventivo.
        </p>
      )}

      <Button variant="hero" size="lg" className="mt-6 w-full" onClick={onNext}>
        Invia richiesta <ArrowRight />
      </Button>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        Nessun pagamento ora. Riceverai il link di pagamento solo dopo che la tua richiesta sarà approvata dal nostro team.
      </p>
    </div>
  );
}

function SectionHeader({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle: string }) {
  return (
    <div className="mt-10 border-t border-border pt-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{eyebrow}</p>
      <h4 className="mt-1 font-display text-xl font-bold tracking-tight sm:text-2xl">{title}</h4>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function AddonCard({ addon, active, onToggle }: { addon: Addon; active: boolean; onToggle: () => void }) {
  const { format } = useCurrency();
  const Icon = addon.icon;
  const isMonthly = addon.period === "/mese";
  return (
    <div
      className={`group relative flex flex-col rounded-2xl border p-5 transition-all duration-200 ${
        active
          ? "border-primary bg-primary/5 shadow-[0_8px_30px_-12px_var(--primary)]"
          : "border-border bg-card hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg"
      } ${addon.featured ? "ring-2 ring-primary/40" : ""}`}
    >
      {addon.featured && (
        <div className="absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary to-primary/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-md">
          <Crown className="h-3 w-3" /> Più scelto
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-right">
          <div className="font-display text-lg font-bold text-foreground">{format(addon.price)}</div>
          {isMonthly && <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">/mese</div>}
        </div>
      </div>

      <h5 className="mt-4 text-base font-semibold leading-tight">{addon.label}</h5>
      <p className="mt-1.5 text-sm text-muted-foreground">{addon.desc}</p>

      {addon.features && (
        <ul className="mt-4 space-y-1.5">
          {addon.features.map(f => (
            <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={onToggle}
        className={`mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors ${
          active
            ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
            : "border-border bg-background hover:border-primary hover:text-primary"
        }`}
      >
        {active ? (<><Minus className="h-4 w-4" /> Rimuovi dal progetto</>) : (<><Plus className="h-4 w-4" /> Aggiungi al progetto</>)}
      </button>
    </div>
  );
}

function DomainSection({
  domain, setDomain,
}: {
  domain: DomainSelection | null;
  setDomain: (d: DomainSelection | null) => void;
}) {
  const [value, setValue] = useState(domain?.name ?? "");

  return (
    <div className="mt-5 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.04] to-card p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Globe className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h5 className="text-base font-semibold leading-tight">Acquisto dominio</h5>
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
              <Star className="h-3 w-3" /> Consigliato
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Cerchiamo, acquistiamo e configuriamo il dominio per il tuo nuovo sito. Tu scegli il nome, noi pensiamo al resto.
          </p>

          {domain ? (
            <div className="mt-4 flex items-center justify-between rounded-xl border border-primary bg-primary/5 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate font-semibold">{domain.name}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setDomain(null); setValue(""); }}
                className="shrink-0 rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
              >
                Cambia
              </button>
            </div>
          ) : (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Input
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder="es. miosito.ch"
                className="flex-1"
              />
              <Button
                onClick={() => {
                  const trimmed = value.trim();
                  if (trimmed) setDomain({ name: trimmed });
                }}
                disabled={!value.trim()}
              >
                Conferma
              </Button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Non hai ancora un dominio? Nessun problema. Lo acquistiamo, lo configuriamo e lo colleghiamo noi al tuo sito.
      </p>
    </div>
  );
}


function EnterpriseCard({ solution }: { solution: (typeof ENTERPRISE_SOLUTIONS)[number] }) {
  const Icon = solution.icon;
  return (
    <div className="group relative flex flex-col rounded-2xl border border-border bg-gradient-to-br from-card to-background p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg">
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <span className="rounded-full border border-border bg-background px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Su richiesta
        </span>
      </div>
      <h5 className="mt-4 text-base font-semibold leading-tight">{solution.title}</h5>
      <p className="mt-1.5 text-sm text-muted-foreground">{solution.desc}</p>
      <a
        href={ENTERPRISE_CONTACT}
        className="mt-auto pt-5"
      >
        <span className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary/40 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground">
          Richiedi consulenza <ArrowRight className="h-4 w-4" />
        </span>
      </a>
    </div>
  );
}


function RequestStep({ subtotal, pkg, addons, domain, onNext }: {
  subtotal: number;
  pkg: "landing" | "business";
  addons: Record<string, boolean>;
  domain: DomainSelection | null;
  onNext: () => void;
}) {
  const { format, currency } = useCurrency();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    company: "",
    notes: "",
    privacy: false,
    marketing: false,
  });
  const pkgName = PACKAGES[pkg].name;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.privacy) return;
    setSubmitting(true);
    const selectedAddons = ADDONS
      .filter((a) => addons[a.id])
      .map((a) => `${a.label} (${format(a.price)}${a.period === "/mese" ? "/mese" : ""})`);
    const lines = [
      "Ciao! Ho una nuova richiesta per FlashWeb Agency:",
      "",
      `📦 Pacchetto: ${pkgName}`,
      `💰 Preventivo: ${format(subtotal)}`,
      "",
      `👤 Nome: ${form.customer_name}`,
      `📧 Email: ${form.customer_email}`,
      `📞 Telefono: ${form.customer_phone}`,
      ...(form.company ? [`🏢 Azienda/Progetto: ${form.company}`] : []),
      ...(domain?.name ? [`🌐 Dominio richiesto: ${domain.name}`] : []),
      ...(selectedAddons.length ? [
        "",
        "➕ Add-on selezionati:",
        ...selectedAddons.map((a) => `• ${a}`),
      ] : []),
      ...(form.notes ? ["", "📝 Note:", form.notes] : []),
    ];
    const text = lines.join("\n");
    window.open(`https://wa.me/41782369200?text=${encodeURIComponent(text)}`, "_blank");
    setSubmitting(false);
    onNext();
  }

  return (
    <form onSubmit={handleSubmit} className="p-8">
      <Eyebrow>Step 2 di 2 · Richiesta</Eyebrow>
      <h3 className="mt-3 text-2xl font-bold">Invia la tua richiesta</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {pkgName} · <span className="font-bold text-foreground">Preventivo {format(subtotal)}</span>
      </p>
      <p className="mt-2 text-sm text-muted-foreground">
        Compila i tuoi dati: valuteremo la richiesta e ti risponderemo entro 24h. Il pagamento avverrà solo dopo l'approvazione.
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Nome e cognome</label>
          <Input required placeholder="Mario Rossi" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Email</label>
          <Input type="email" required placeholder="mario@azienda.ch" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Telefono</label>
          <Input type="tel" required placeholder="+41 79 123 45 67" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Azienda / Progetto</label>
          <Input placeholder="Nome dell'azienda o del progetto" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Note aggiuntive (opzionale)</label>
          <textarea
            rows={4}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="Raccontaci di più sul tuo progetto…"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </div>

      <div className="mt-6 space-y-3 rounded-xl border border-border/60 bg-card/30 p-4">
        <label className="flex items-start gap-3 text-xs leading-relaxed text-muted-foreground">
          <input
            type="checkbox"
            required
            checked={form.privacy}
            onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
          />
          <span>
            Accetto la{" "}
            <Link to="/privacy-policy" target="_blank" className="text-primary underline hover:opacity-80">
              Privacy Policy
            </Link>{" "}
            e autorizzo il trattamento dei miei dati per essere ricontattato. <span className="text-primary">*</span>
          </span>
        </label>
        <label className="flex items-start gap-3 text-xs leading-relaxed text-muted-foreground">
          <input
            type="checkbox"
            checked={form.marketing}
            onChange={(e) => setForm({ ...form, marketing: e.target.checked })}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
          />
          <span>Desidero ricevere comunicazioni, offerte e aggiornamenti da FlashWeb.</span>
        </label>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <Lock className="h-3 w-3 text-primary" />
        Nessun addebito ora. Il pagamento sarà richiesto solo dopo l'approvazione della richiesta.
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        I tuoi dati sono trattati in modo sicuro. Nessuno spam — solo informazioni utili per il tuo progetto. Puoi richiedere la cancellazione in qualsiasi momento.
      </p>

      <Button variant="hero" size="lg" type="submit" className="mt-4 w-full" disabled={submitting || !form.privacy}>
        {submitting ? "Invio in corso…" : "Invia richiesta"} {!submitting && <ArrowRight />}
      </Button>
    </form>
  );
}

function SubmittedStep({ onClose }: { onClose: () => void }) {
  return (
    <div className="p-8 text-center sm:p-12">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
        <CheckCircle2 className="h-8 w-8" />
      </div>
      <h3 className="mt-6 font-display text-2xl font-bold sm:text-3xl">Richiesta inviata con successo</h3>
      <p className="mt-3 text-sm text-muted-foreground sm:text-base">
        Grazie! Abbiamo ricevuto la tua richiesta e la valuteremo entro 24 ore.
        Ti contatteremo via email per confermare i dettagli e, una volta approvata,
        riceverai il link sicuro per completare il pagamento e avviare il progetto.
      </p>
      <div className="mt-6 grid gap-3 text-left sm:grid-cols-3">
        {[
          { t: "1. Valutazione", d: "Analizziamo la tua richiesta entro 24h." },
          { t: "2. Approvazione", d: "Ti inviamo conferma e link di pagamento." },
          { t: "3. Avvio progetto", d: "Iniziamo subito dopo il pagamento." },
        ].map(s => (
          <div key={s.t} className="rounded-xl border border-border bg-background/50 p-4">
            <p className="text-sm font-semibold">{s.t}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
          </div>
        ))}
      </div>
      <Button variant="hero" size="lg" className="mt-8" onClick={onClose}>
        Chiudi
      </Button>
    </div>
  );
}

const ONBOARDING_STEPS = [
  { icon: Palette, title: "Brand", desc: "Logo, colori, font preferiti" },
  { icon: Layout, title: "Struttura", desc: "Pagine necessarie" },
  { icon: FileEdit, title: "Contenuti", desc: "Testi o assistente AI" },
  { icon: KeyRound, title: "Dominio", desc: "Accessi hosting/dominio" },
] as const;

function OnboardingWizard({ onDone }: { onDone: () => void }) {
  const [idx, setIdx] = useState(0);
  const [data, setData] = useState({
    brandName: "", color: "#FF6B00",
    pages: { home: true, about: false, services: false, contact: false },
    content: "", useAI: false,
    domain: "",
  });

  function next() {
    if (idx < ONBOARDING_STEPS.length - 1) setIdx(idx + 1);
    else onDone();
  }

  const StepIcon = ONBOARDING_STEPS[idx].icon;

  return (
    <div className="p-8">
      <Eyebrow>Step 3 di 3 · Onboarding · {idx + 1}/4</Eyebrow>
      <div className="mt-4 flex gap-2">
        {ONBOARDING_STEPS.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= idx ? "bg-primary" : "bg-border"}`} />
        ))}
      </div>

      <div className="mt-6 flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <StepIcon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-2xl font-bold">{ONBOARDING_STEPS[idx].title}</h3>
          <p className="text-sm text-muted-foreground">{ONBOARDING_STEPS[idx].desc}</p>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {idx === 0 && (
          <>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Nome brand</label>
              <Input required value={data.brandName} onChange={e => setData({ ...data, brandName: e.target.value })} placeholder="Es. Acme SA" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Colore principale</label>
              <input type="color" value={data.color} onChange={e => setData({ ...data, color: e.target.value })} className="h-10 w-full cursor-pointer rounded-md border border-border bg-transparent" />
            </div>
            <button type="button" className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border bg-background/50 px-4 py-6 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground">
              <Upload className="h-4 w-4" /> Carica logo (PNG/SVG)
            </button>
          </>
        )}
        {idx === 1 && (
          <div className="grid grid-cols-2 gap-3">
            {(Object.keys(data.pages) as (keyof typeof data.pages)[]).map(p => (
              <label key={p} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${data.pages[p] ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                <input
                  type="checkbox"
                  checked={data.pages[p]}
                  onChange={e => setData({ ...data, pages: { ...data.pages, [p]: e.target.checked } })}
                  className="h-4 w-4 accent-[var(--primary)]"
                />
                <span className="text-sm capitalize">{p === "about" ? "Chi siamo" : p === "services" ? "Servizi" : p === "contact" ? "Contatti" : "Home"}</span>
              </label>
            ))}
          </div>
        )}
        {idx === 2 && (
          <>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-border p-4 transition-colors hover:border-primary/40">
              <input
                type="checkbox"
                checked={data.useAI}
                onChange={e => setData({ ...data, useAI: e.target.checked })}
                className="h-4 w-4 accent-[var(--primary)]"
              />
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm">Attiva assistente copywriter AI</span>
            </label>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Contenuti / note</label>
              <textarea
                value={data.content}
                onChange={e => setData({ ...data, content: e.target.value })}
                rows={5}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Incolla i testi o descrivi l'azienda…"
              />
            </div>
          </>
        )}
        {idx === 3 && (
          <>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Dominio</label>
              <Input required value={data.domain} onChange={e => setData({ ...data, domain: e.target.value })} placeholder="azienda.ch" />
            </div>
            <p className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-xs text-muted-foreground">
              Non hai un dominio? Lo acquistiamo noi per te (incluso nella tariffa).
            </p>
          </>
        )}
      </div>

      <div className="mt-6 flex gap-3">
        {idx > 0 && (
          <Button variant="outlineHero" size="lg" onClick={() => setIdx(idx - 1)}>Indietro</Button>
        )}
        <Button variant="hero" size="lg" className="flex-1" onClick={next}>
          {idx < ONBOARDING_STEPS.length - 1 ? "Continua" : "Conferma e avvia le 48 ore"} <ArrowRight />
        </Button>
      </div>
    </div>
  );
}

function ClientDashboard({ onClose }: { onClose: () => void }) {
  const stages = [
    { name: "Analisi briefing", done: true },
    { name: "Sviluppo bozza", done: false, active: true },
    { name: "Revisioni finali", done: false },
    { name: "Pronto al lancio", done: false },
  ];

  return (
    <div className="p-8">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-2xl font-bold">Tutti i dati ricevuti!</h3>
          <p className="text-sm text-primary">Le 48 ore partono ora.</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {stages.map((s, i) => (
          <div key={i} className={`flex items-center gap-3 rounded-xl border p-4 ${s.active ? "border-primary/60 bg-primary/5" : s.done ? "border-border bg-card" : "border-border bg-background/30"}`}>
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${s.done ? "bg-primary text-primary-foreground" : s.active ? "border border-primary text-primary" : "border border-border text-muted-foreground"}`}>
              {s.done ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`text-sm ${s.active ? "font-semibold" : ""}`}>{s.name}</span>
            {s.active && <span className="ml-auto text-xs text-primary">in corso…</span>}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background/40 p-4">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Anteprima Loom</p>
        <div className="mt-3 flex aspect-video items-center justify-center rounded-lg border border-dashed border-border bg-card text-xs text-muted-foreground">
          Video di avanzamento in arrivo a 12h
        </div>
      </div>

      <Button variant="outlineHero" size="lg" className="mt-6 w-full" onClick={onClose}>
        Chiudi dashboard
      </Button>
    </div>
  );
}
